import type { CmpConfigPayload, ConsentState } from "./types";
import { track, alias, isTracked, hostOf, srcOf, type DbgVia } from "./debug";

type Held = { el: HTMLElement; type: "script" | "iframe" | "img"; reason: string };

const held: Held[] = [];
const heldSet = new WeakSet<Element>();
const internalHosts = new Set<string>();

/** Hosts serving the CMP itself (never reported as unknown third-party). */
export function setInternalHost(host: string) { if (host) internalHosts.add(host); }

function hold(item: Held, via: DbgVia, cfg: CmpConfigPayload) {
  if (!heldSet.has(item.el)) { heldSet.add(item.el); held.push(item); }
  const d = describe(cfg, item.el);
  track(item.el, { state: "blocked", via, reason: item.reason, vendor: d.vendor, purposes: d.purposes });
}

function describe(cfg: CmpConfigPayload, el: Element) {
  const src = srcOf(el);
  return {
    vendor: el.getAttribute("data-cmp-vendor") || el.getAttribute("data-cmp-custom") || (src ? hostVendor(cfg, src) : null),
    purposes: el.getAttribute("data-cmp-purposes")
  };
}

/** Record an element that was activated (after consent) or allowed immediately. */
function markActive(cfg: CmpConfigPayload, el: Element, via: DbgVia) {
  const d = describe(cfg, el);
  track(el, { state: isTracked(el) ? "activated" : "allowed", via, vendor: d.vendor, purposes: d.purposes, reason: null });
}
let observer: MutationObserver | null = null;
let patched = false;
const originalCreateElement = Document.prototype.createElement;
const originalAppendChild = Node.prototype.appendChild;
const originalInsertBefore = Node.prototype.insertBefore;

function vendorAllowed(state: ConsentState | null, vendorKey: string): boolean {
  if (!state) return false;
  if (/^\d+$/.test(vendorKey)) return !!state.vendorConsents[+vendorKey];
  return !!state.customVendors[vendorKey];
}

function purposesAllowed(state: ConsentState | null, purposes: string): boolean {
  if (!state) return false;
  return purposes.split(",").map((p) => p.trim()).filter(Boolean).every((p) => {
    if (/^\d+$/.test(p)) return !!state.purposeConsents[+p];
    return p === "necessary" || !!state.customPurposes[p];
  });
}

function hostVendor(cfg: CmpConfigPayload, url: string): string | null {
  try {
    const host = new URL(url, location.href).hostname;
    for (const [domain, key] of Object.entries(cfg.trackerDomainMap || {})) {
      if (host === domain || host.endsWith("." + domain)) return key;
    }
    for (const cv of cfg.customVendors || []) {
      if (cv.domains.some((d) => host === d || host.endsWith("." + d))) return cv.key;
    }
  } catch { /* ignore */ }
  return null;
}

function shouldBlock(cfg: CmpConfigPayload, state: ConsentState | null, el: Element): string | null {
  const vendor = el.getAttribute("data-cmp-vendor");
  const custom = el.getAttribute("data-cmp-custom");
  const purposes = el.getAttribute("data-cmp-purposes");
  if (vendor && !vendorAllowed(state, vendor)) return `vendor:${vendor}`;
  if (custom && !vendorAllowed(state, custom)) return `custom:${custom}`;
  if (purposes && !purposesAllowed(state, purposes)) return `purposes:${purposes}`;
  const src = el.getAttribute("data-cmp-src") || el.getAttribute("src") || "";
  if (src) {
    const key = hostVendor(cfg, src);
    if (key && !vendorAllowed(state, key)) return `host:${key}`;
  }
  // Auto-block plain scripts/iframes whose host maps to a known tracker when no consent yet
  if (!vendor && !custom && !purposes && src && (el.tagName === "SCRIPT" || el.tagName === "IFRAME" || el.tagName === "IMG")) {
    const key = hostVendor(cfg, src);
    if (key && !vendorAllowed(state, key)) return `auto:${key}`;
  }
  return null;
}

function placeholderFor(iframe: HTMLIFrameElement) {
  const wrap = document.createElement("div");
  wrap.setAttribute("data-cmp-placeholder", "1");
  wrap.style.cssText = "display:flex;align-items:center;justify-content:center;min-height:220px;background:#f8fafc;border:2px dashed #0f766e;border-radius:12px;color:#334155;font:14px system-ui;padding:16px;text-align:center;";
  wrap.innerHTML = `<div><div style="margin-bottom:8px">Externer Inhalt blockiert (z. B. YouTube). Bitte stimmen Sie den Marketing-Cookies zu.</div><button type="button" style="padding:8px 12px;border-radius:8px;border:0;background:#0f766e;color:#fff;cursor:pointer">Inhalt laden</button></div>`;
  wrap.querySelector("button")!.addEventListener("click", () => {
    const src = iframe.getAttribute("data-cmp-src");
    if (src) iframe.src = src;
    iframe.style.display = "";
    wrap.replaceWith(iframe);
  });
  return wrap;
}

function activateScript(script: HTMLScriptElement) {
  const s = document.createElement("script");
  for (const attr of Array.from(script.attributes)) {
    if (attr.name === "type") continue;
    s.setAttribute(attr.name, attr.value);
  }
  const src = script.src || script.getAttribute("data-cmp-src");
  if (src) s.src = src;
  else s.textContent = script.textContent;
  alias(script, s);
  script.parentNode?.replaceChild(s, script);
}

function activateIframe(iframe: HTMLIFrameElement) {
  const src = iframe.getAttribute("data-cmp-src");
  // Re-assigning an identical src re-navigates the iframe; scanAndHold runs on every mutation.
  if (src && iframe.getAttribute("src") !== src) iframe.src = src;
  iframe.style.display = "";
  const prev = iframe.previousElementSibling;
  if (prev?.hasAttribute("data-cmp-placeholder")) prev.remove();
}

export function scanAndHold(cfg: CmpConfigPayload, state: ConsentState | null) {
  document.querySelectorAll("script[type='text/plain'][data-cmp-vendor],script[type='text/plain'][data-cmp-custom],script[type='text/plain'][data-cmp-purposes],script[type='text/plain'][data-cmp-src]").forEach((el) => {
    const reason = shouldBlock(cfg, state, el);
    const via: DbgVia = el.hasAttribute("data-cmp-auto") ? "hook" : "manual";
    if (reason) hold({ el: el as HTMLElement, type: "script", reason }, via, cfg);
    else { markActive(cfg, el, via); activateScript(el as HTMLScriptElement); }
  });

  document.querySelectorAll("iframe[data-cmp-src],iframe[data-cmp-vendor],iframe[data-cmp-custom]").forEach((el) => {
    const iframe = el as HTMLIFrameElement;
    const reason = shouldBlock(cfg, state, iframe);
    if (reason) {
      if (!iframe.getAttribute("data-cmp-src") && iframe.src) iframe.setAttribute("data-cmp-src", iframe.src);
      iframe.removeAttribute("src");
      iframe.style.display = "none";
      if (!iframe.previousElementSibling?.hasAttribute("data-cmp-placeholder")) {
        iframe.parentNode?.insertBefore(placeholderFor(iframe), iframe);
      }
      hold({ el: iframe, type: "iframe", reason }, iframe.hasAttribute("data-cmp-auto") ? "hook" : "manual", cfg);
    } else {
      if (iframe.getAttribute("data-cmp-src") && !iframe.getAttribute("src")) markActive(cfg, iframe, "manual");
      activateIframe(iframe);
    }
  });
}

export function releaseAll(cfg: CmpConfigPayload, state: ConsentState) {
  const remaining: Held[] = [];
  for (const item of held) {
    if (!shouldBlock(cfg, state, item.el)) {
      heldSet.delete(item.el);
      markActive(cfg, item.el, item.el.hasAttribute("data-cmp-auto") ? "hook" : "manual");
      if (item.type === "script") activateScript(item.el as HTMLScriptElement);
      else if (item.type === "iframe") activateIframe(item.el as HTMLIFrameElement);
    } else remaining.push(item);
  }
  held.length = 0;
  held.push(...remaining);
  scanAndHold(cfg, state);
}

export function startAutoBlocking(cfg: CmpConfigPayload, getState: () => ConsentState | null) {
  if (patched) return;
  patched = true;

  Document.prototype.createElement = function (this: Document, tagName: string, options?: ElementCreationOptions) {
    return originalCreateElement.call(this, tagName, options);
  } as typeof Document.prototype.createElement;

  function intercept(node: Node, insert: () => Node): Node {
    if (node instanceof HTMLElement) {
      const state = getState();
      const tag = node.tagName;
      if (tag === "SCRIPT" || tag === "IFRAME" || tag === "IMG") {
        // Ensure src present for host matching
        const reason = shouldBlock(cfg, state, node);
        if (reason) {
          if (tag === "SCRIPT") {
            const script = node as HTMLScriptElement;
            if (script.src) {
              script.setAttribute("data-cmp-src", script.src);
              script.removeAttribute("src");
            }
            script.type = "text/plain";
            const manual = MANUAL_ATTRS.some((a) => script.hasAttribute(a));
            if (!manual) script.setAttribute("data-cmp-auto", "1");
            hold({ el: script, type: "script", reason }, manual ? "manual" : "hook", cfg);
          } else if (tag === "IFRAME") {
            const iframe = node as HTMLIFrameElement;
            if (iframe.src) {
              iframe.setAttribute("data-cmp-src", iframe.src);
              iframe.removeAttribute("src");
            }
            iframe.style.display = "none";
            const manual = MANUAL_ATTRS.some((a) => iframe.hasAttribute(a));
            if (!manual) iframe.setAttribute("data-cmp-auto", "1");
            hold({ el: iframe, type: "iframe", reason }, manual ? "manual" : "hook", cfg);
            const result = insert();
            if (!iframe.previousElementSibling?.hasAttribute("data-cmp-placeholder")) {
              iframe.parentNode?.insertBefore(placeholderFor(iframe), iframe);
            }
            return result;
          } else {
            // <img> requests fire on src assignment, before insertion — cannot be held here.
            recordThirdParty(cfg, state, node, "hook", "img: request fired on src assignment");
          }
        } else if (!isTracked(node)) {
          recordThirdParty(cfg, state, node, "hook");
        }
      } else if (tag === "LINK") {
        recordThirdParty(cfg, state, node, "hook");
      }
    }
    return insert();
  }

  Node.prototype.appendChild = function <T extends Node>(node: T): T {
    return intercept(node, () => originalAppendChild.call(this, node)) as T;
  };
  Node.prototype.insertBefore = function <T extends Node>(node: T, ref: Node | null): T {
    return intercept(node, () => originalInsertBefore.call(this, node, ref)) as T;
  };

  // Elements that were already in the DOM before the blocker was active.
  document.querySelectorAll(WATCH).forEach((el) => recordThirdParty(cfg, getState(), el, "preexisting"));

  observer = new MutationObserver((records) => {
    const state = getState();
    for (const r of records) {
      r.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;
        if (n.matches(WATCH)) recordThirdParty(cfg, state, n, "observer");
        if (n.firstElementChild) n.querySelectorAll(WATCH).forEach((c) => recordThirdParty(cfg, state, c, "observer"));
      });
    }
    scanAndHold(cfg, state);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

const MANUAL_ATTRS = ["data-cmp-vendor", "data-cmp-custom", "data-cmp-purposes"];
const WATCH = "script[src],iframe[src],img[src],link[href]";
const LINK_REL = /stylesheet|preload|prefetch|preconnect|modulepreload|icon/i;

/**
 * Debug-only classification of third-party elements that did not go through
 * the manual blocking path: allowed (vendor consented), leaked (mapped vendor
 * without consent but loaded anyway) or unmatched (unknown domain).
 */
function recordThirdParty(cfg: CmpConfigPayload, state: ConsentState | null, el: Element, via: DbgVia, why?: string) {
  if (isTracked(el) || el.hasAttribute("data-adcmp-internal") || el.hasAttribute("data-cmp-src")) return;
  if (el.tagName === "SCRIPT" && (el as HTMLScriptElement).type === "text/plain") return;
  if (el.tagName === "LINK" && !LINK_REL.test(el.getAttribute("rel") || "")) return;
  const src = srcOf(el);
  const host = hostOf(src);
  if (!host || host === location.hostname || internalHosts.has(host)) return;
  const vendor = hostVendor(cfg, src);
  const state_ = vendor ? (vendorAllowed(state, vendor) ? "allowed" : "leaked") : "unmatched";
  track(el, { state: state_, via, vendor, reason: state_ === "leaked" ? (why || (via === "preexisting" ? "loaded before CMP" : "inserted without hook")) : null });
}

export function cookiePatternsToPurge(cfg: CmpConfigPayload, state: ConsentState): string[] {
  const patterns: string[] = [];
  for (const cv of cfg.customVendors || []) {
    if (!state.customVendors[cv.key]) patterns.push(...(cv.cookiePatterns || []));
  }
  return patterns;
}
